import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../api/client";
import { formatUSD, shortAddr } from "../lib/format";
import { AdminTabs } from "./AdminDashboard";

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [creditModal, setCreditModal] = useState(null);
    const [creditAmount, setCreditAmount] = useState("");

    const load = async () => {
        try {
            const r = await api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
            setUsers(r.data);
        } catch {}
    };
    useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [q]);

    const credit = async () => {
        if (!creditModal) return;
        try {
            await api.post(`/admin/users/${creditModal.id}/credit`, { amount: parseFloat(creditAmount), note: "admin adjustment" });
            toast.success(`${parseFloat(creditAmount) >= 0 ? "Credited" : "Debited"} ${creditModal.email}`);
            setCreditModal(null); setCreditAmount(""); load();
        } catch (e) {
            toast.error("Adjustment failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    const toggleDisabled = async (u) => {
        try {
            await api.patch(`/admin/users/${u.id}/disable`, { disabled: !u.disabled });
            toast.success(`${u.email} ${u.disabled ? "enabled" : "disabled"}`);
            load();
        } catch (e) {
            toast.error("Failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-[1480px] mx-auto px-4 lg:px-8 py-10 grid gap-6">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Admin · Users</div>
                    <h1 className="text-3xl font-medium tracking-tighter mt-2">User Management</h1>
                </div>
                <AdminTabs />

                <input
                    data-testid="admin-users-search"
                    placeholder="search by email…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="bg-[#0A0A0C] border border-[#1F1F24] focus:border-cyan-500/50 outline-none px-4 py-2.5 rounded-sm font-mono text-sm"
                />

                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm overflow-x-auto">
                    <table className="w-full" data-testid="admin-users-table">
                        <thead>
                            <tr className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                                {["Email", "Role", "Wallet", "Deposited", "Locked", "Free", "Active swaps", "Status", "Actions"].map((h) => (
                                    <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 && (
                                <tr><td colSpan={9} className="text-center py-6 font-mono text-[11px] text-zinc-600">no users</td></tr>
                            )}
                            {users.map((u) => {
                                const w = u.wallet || {};
                                const free = (w.deposited || 0) - (w.locked || 0);
                                return (
                                    <tr key={u.id} className="border-t border-[#1F1F24] font-mono text-xs">
                                        <td className="px-4 py-2 text-zinc-100">{u.email}</td>
                                        <td className="px-4 py-2 text-zinc-400">{u.role}</td>
                                        <td className="px-4 py-2 text-zinc-500">{shortAddr(u.wallet_address)}</td>
                                        <td className="px-4 py-2 text-zinc-200">{formatUSD(w.deposited || 0)}</td>
                                        <td className="px-4 py-2 text-amber-400">{formatUSD(w.locked || 0)}</td>
                                        <td className="px-4 py-2 text-cyan-400">{formatUSD(free)}</td>
                                        <td className="px-4 py-2 text-zinc-300">{u.active_swaps}</td>
                                        <td className="px-4 py-2">
                                            <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${u.disabled ? "bg-rose-500/20 text-rose-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                                                {u.disabled ? "DISABLED" : "ACTIVE"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-right whitespace-nowrap">
                                            <button data-testid={`admin-credit-${u.id}`} onClick={() => setCreditModal(u)} className="text-[10px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300 mr-3">credit</button>
                                            <button data-testid={`admin-disable-${u.id}`} onClick={() => toggleDisabled(u)} className={`text-[10px] uppercase tracking-widest ${u.disabled ? "text-emerald-400 hover:text-emerald-300" : "text-rose-400 hover:text-rose-300"}`}>
                                                {u.disabled ? "enable" : "disable"}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {creditModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 grid place-items-center px-4" data-testid="admin-credit-modal">
                    <div className="w-full max-w-md bg-[#0A0A0C] border border-[#1F1F24] rounded-sm">
                        <div className="px-5 py-4 border-b border-[#1F1F24]">
                            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">Credit / Debit</div>
                            <div className="text-lg font-medium mt-1">{creditModal.email}</div>
                            <div className="text-[10px] font-mono text-zinc-500 mt-1">Current: {formatUSD(creditModal.wallet?.deposited || 0)}</div>
                        </div>
                        <div className="p-5 grid gap-3">
                            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Amount (USDC, positive = credit, negative = debit)</label>
                            <input
                                data-testid="admin-credit-amount-input"
                                type="number"
                                value={creditAmount}
                                onChange={(e) => setCreditAmount(e.target.value)}
                                className="bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-base"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2 p-4 border-t border-[#1F1F24]">
                            <button onClick={() => { setCreditModal(null); setCreditAmount(""); }} className="py-2.5 border border-[#1F1F24] hover:border-zinc-600 text-zinc-300 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancel</button>
                            <button data-testid="admin-credit-confirm-button" onClick={credit} disabled={!creditAmount || creditAmount === "0"} className="py-2.5 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 text-black text-xs uppercase tracking-widest font-semibold rounded-sm">Apply</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
